import * as React from "react";
import { observer, inject } from "mobx-react";
import { TypeaheadSelection } from "uitk-react-typeahead";
import { LocationProps } from "./typings";
import { LocationField } from "../LocationField/LocationField";
import { FlightWizardState, PackageWizardState, LOBState, LxWizardState, CarWizardState } from "stores/wizard/state";
import { SwapLocations } from "../SwapLocations/SwapLocations";
import { UitkLayoutGrid, UitkLayoutGridItem } from "uitk-react-layout-grid";
import { useLocalization } from "@shared-ui/localization-context";
import { ACTION_DONE, getTracker } from "utils/TrackingUtils";

export const Location: React.FunctionComponent<LocationProps> = inject(
  "context",
  "routeStore",
  "analytics"
)(
  observer(
    ({
      lobState,
      context,
      setInputsRefs,
      routeStore,
      originField,
      destinationField,
      colSpan,
      multiCityFlight,
      multiLegIndex,
      onewayFlight,
      showSwapLocationsControl,
      analytics,
    }) => {
      if (!context || !routeStore) {
        // Check for injected stores
        return null;
      }

      const useLocationConfiguration = (index: number | undefined, state: LOBState) => {
        if (multiCityFlight && state.config.multiCityFlight.location) {
          return state.config.multiCityFlight.location; // multicity first leg
        }

        if (
          index !== undefined &&
          state.config.multiCityFlight &&
          (state instanceof FlightWizardState || state instanceof PackageWizardState)
        ) {
          return state.config.multiCityFlight.legs[index].location; // multicity additional legs
        }

        if (state instanceof PackageWizardState && onewayFlight && state.config.onewayFlight?.location) {
          return state.config.onewayFlight.location; // oneway flight
        }

        if (state instanceof LxWizardState) {
          return state.config.location;
        }

        return state.tabSelected === false
          ? state.subLOBState === "toAirport"
            ? state.config.gtConfig.toAirportLocation
            : state.config.gtConfig.location
          : state.config.location;
      };

      const locationConfig = useLocationConfiguration(multiLegIndex, lobState);
      const locationState =
        multiLegIndex !== undefined &&
        (lobState instanceof FlightWizardState || lobState instanceof PackageWizardState) &&
        lobState.listOfAdditionalLegs
          ? lobState.listOfAdditionalLegs[multiLegIndex].location
          : lobState.location;

      const { formatText } = useLocalization();

      // TODO: Consolidate functions doing related logic.

      const onOriginLocationFieldUpdate = (selection: TypeaheadSelection) => {
        if (lobState.updateOriginSelection) {
          lobState.updateOriginSelection(selection, multiLegIndex);
        }

        tracker.trackEvent(_origin + ACTION_DONE);
      };

      const onDestinationLocationFieldUpdate = (selection: TypeaheadSelection) => {
        lobState.updateDestinationSelection(selection, multiLegIndex);

        tracker.trackEvent(_destination + ACTION_DONE);
      };

      const swapOriginAndDestination = () => {
        if (lobState.swapLocations) {
          lobState.swapLocations(multiLegIndex);
        }
      };

      const getDestinationValue = () => {
        const hasSameOriginAndDestination =
          locationState.origin.metaData.ttla === locationState.destination.metaData.ttla;
        const isCarWizardState = lobState instanceof CarWizardState;

        return !isCarWizardState && hasSameOriginAndDestination ? "" : locationState.destination.value;
      };

      const track = (event: string) => {
        tracker.trackEvent(event);
      };

      const originInvalidMessage = lobState.originInvalidKey ? formatText(lobState.originInvalidKey) : "";
      const destinationInvalidMessage = lobState.destinationInvalidKey
        ? formatText(lobState.destinationInvalidKey)
        : "";

      const locationClassWithSwap = showSwapLocationsControl ? "locationWithSwap" : "";
      const locationFieldCols = originField && destinationField ? 1 : 2;

      const tracker = getTracker(lobState.trackingEnabled, analytics, lobState.moduleName);
      const _origin = "origin.";
      const _destination = "destination.";
      const destinationValue = getDestinationValue();

      return (
        <UitkLayoutGridItem colSpan={colSpan}>
          <div>
            <UitkLayoutGrid
              columns={{ small: 1, medium: 2 }}
              space="three"
              className={`Location ${locationClassWithSwap}`}
            >
              {originField && locationConfig.origin && (
                <UitkLayoutGridItem colSpan={{ small: 1, medium: locationFieldCols }}>
                  <div>
                    <LocationField
                      setInputsRefs={setInputsRefs}
                      context={context}
                      onUpdate={onOriginLocationFieldUpdate}
                      icon={locationConfig.origin.icon}
                      name={
                        lobState instanceof FlightWizardState ||
                        (lobState instanceof PackageWizardState && lobState.isMulticityFlight())
                          ? ""
                          : locationConfig.origin.inputName
                      }
                      label={formatText(locationConfig.origin.labelToken)}
                      typeaheadPlaceholder={formatText(locationConfig.origin.typeaheadLabelToken)}
                      typeaheadEmptyResultPlaceholder={formatText(
                        locationConfig.origin.typeaheadEmptyResultPlaceholder
                      )}
                      invalid={originInvalidMessage}
                      value={locationState.origin.value}
                      essAdapterConfig={locationConfig.origin.essAdapterConfig}
                      stid={`location-field-${locationConfig.origin.inputName}`}
                      config={locationConfig.origin}
                      location={locationState.origin}
                      track={track}
                      trackId={_origin}
                    />
                  </div>
                </UitkLayoutGridItem>
              )}
              {showSwapLocationsControl && <SwapLocations swapLocations={swapOriginAndDestination} />}
              {destinationField && locationConfig.destination && (
                <UitkLayoutGridItem colSpan={{ small: 1, medium: locationFieldCols }}>
                  <div>
                    <LocationField
                      setInputsRefs={setInputsRefs}
                      context={context}
                      onUpdate={onDestinationLocationFieldUpdate}
                      icon={locationConfig.destination.icon}
                      name={
                        lobState instanceof FlightWizardState ||
                        (lobState instanceof PackageWizardState && lobState.isMulticityFlight())
                          ? ""
                          : locationConfig.destination.inputName
                      }
                      label={formatText(locationConfig.destination.labelToken)}
                      typeaheadPlaceholder={formatText(locationConfig.destination.typeaheadLabelToken)}
                      typeaheadEmptyResultPlaceholder={formatText(
                        locationConfig.destination.typeaheadEmptyResultPlaceholder
                      )}
                      invalid={destinationInvalidMessage}
                      value={destinationValue}
                      essAdapterConfig={locationConfig.destination.essAdapterConfig}
                      stid={`location-field-${locationConfig.destination.inputName}`}
                      config={locationConfig.destination}
                      location={locationState.destination}
                      track={track}
                      trackId={_destination}
                    />
                  </div>
                </UitkLayoutGridItem>
              )}
            </UitkLayoutGrid>
          </div>
        </UitkLayoutGridItem>
      );
    }
  )
);
